/*
  Babblee · search overlay behaviour
  ===========================================================================
  Results come from Shopify's own predictive search endpoint, so what appears
  here is what the search page will show — a suggestion list built from a
  different source than the results it leads to is a list that lies.

  Three states, and all three are designed rather than left blank: nothing
  typed (recently viewed and a few ways in), typing (last results held while the
  next arrive), and nothing found (the query named back, with a way onward).
*/
(() => {
  const overlay = document.querySelector('[data-bb-search-overlay]');
  if (!overlay) return;

  const panel = overlay.querySelector('[data-bb-search-panel]');
  const form = overlay.querySelector('[data-bb-search-form]');
  const field = overlay.querySelector('[data-bb-search-field]');
  const body = overlay.querySelector('[data-bb-search-body]');
  const allLink = overlay.querySelector('[data-bb-search-all]');
  const RECENT_KEY = 'bb:recent-products';
  const MAX_RECENT = 8;

  let lastFocus = null;
  let active = -1;
  let controller = null;
  let debounce = null;

  /* The predictive endpoint returns a bare decimal — "44.99" — with no symbol,
     so every price in the panel read as a number while the same product showed
     "€44.99" everywhere else on the site. Formatted against the shop's active
     currency, falling back to the raw value rather than to nothing. */
  const money = (value) => {
    if (value == null || value === '') return '';
    const n = Number(String(value).replace(/[^\d.-]/g, ''));
    if (!isFinite(n)) return String(value);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(n);
    } catch { return String(value); }
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  /* ---------------------------------------------------------------------
     Recently viewed
     ------------------------------------------------------------------ */
  const readRecent = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  };
  const rememberProduct = () => {
    // Only on a product page, and only what is needed to draw a row.
    const el = document.querySelector('[data-bb-product-seed]');
    if (!el) return;
    const item = {
      url: el.dataset.url, title: el.dataset.title,
      image: el.dataset.image, price: el.dataset.price,
    };
    if (!item.url || !item.title) return;
    const next = [item, ...readRecent().filter((r) => r.url !== item.url)].slice(0, MAX_RECENT);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };

  /* ---------------------------------------------------------------------
     Rendering
     ------------------------------------------------------------------ */
  const productRow = (p) => `
    <a class="bb-search__row" role="option" href="${esc(p.url)}">
      ${p.image
        ? `<img class="bb-search__thumb" src="${esc(p.image)}" alt="" loading="lazy" width="54" height="54">`
        : '<span class="bb-search__thumb"></span>'}
      <span class="bb-search__text">
        <span class="bb-search__name">${esc(p.title)}</span>
        ${p.meta ? `<span class="bb-search__meta">${esc(p.meta)}</span>` : ''}
      </span>
      ${p.price ? `<span class="bb-search__price">${esc(p.price)}</span>` : ''}
    </a>`;

  const termRow = (t) => `
    <a class="bb-search__row bb-search__row--term" role="option" href="${esc(t.url)}">
      <span class="bb-search__mark">${t.icon}</span>
      <span class="bb-search__text"><span class="bb-search__name">${esc(t.title)}</span></span>
      <span class="bb-search__go"><svg viewBox="0 0 16 16" fill="none"><path d="M6 3.5L10.5 8 6 12.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
    </a>`;

  const ICON_TERM = '<svg viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.6" stroke="currentColor" stroke-width="1.6"/><path d="M10.6 10.6L14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  const ICON_COLL = '<svg viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="9" y="2" width="5" height="5" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="2" y="9" width="5" height="5" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="9" y="9" width="5" height="5" rx="1.5" stroke="currentColor" stroke-width="1.5"/></svg>';

  const group = (label, inner, extra = '') =>
    `<div class="bb-search__group"><p class="bb-search__label">${esc(label)}${extra}</p>${inner}</div>`;

  const readSeed = () => {
    try { return JSON.parse(document.querySelector('[data-bb-search-seed]')?.textContent || '[]'); }
    catch { return []; }
  };

  const renderResting = () => {
    const recent = readRecent();
    let html = '';

    if (recent.length) {
      html += group('Recently viewed',
        recent.slice(0, 4).map(productRow).join(''),
        '<button type="button" class="bb-search__clear" data-bb-search-forget>Clear</button>');
    }

    const ways = [
      { title: 'New in', url: '/collections/all?sort_by=created-descending' },
      { title: 'Newborn 0–3 months', url: '/collections/all' },
      { title: 'Feeding', url: '/collections/all' },
      { title: 'Gifts', url: '/collections/all' },
    ];
    html += group('Browse',
      `<div class="bb-search__chips">${ways.map((w) =>
        `<a class="bb-search__chip" href="${esc(w.url)}">${esc(w.title)}</a>`).join('')}</div>`);

    /* Products, not just ways to reach them. Fewer when something recent is
       already shown, so the panel does not open as a wall of rows. */
    const seed = readSeed();
    if (seed.length) {
      html += group('Popular right now', seed.slice(0, recent.length ? 3 : 5).map(productRow).join(''));
    }

    body.innerHTML = html;
    setActive(-1);
  };

  const renderEmpty = (query) => {
    body.innerHTML = `
      <div class="bb-search__empty">
        <h3>Nothing matched “${esc(query)}”</h3>
        <p>Try a shorter word, or the thing rather than the brand — “bib” finds more than “muslin bib set”.</p>
      </div>`;
    setActive(-1);
  };

  const renderResults = (data, query) => {
    const r = data?.resources?.results || {};
    const products = (r.products || []).slice(0, 6).map((p) => ({
      url: p.url,
      title: p.title,
      /* The endpoint gives featured_image as an object on some shops and a bare
         URL string on others, and either can be missing. All three, in order —
         one product rendered as an empty blue square because only the first was
         being read. */
      image: p.featured_image?.url
        || (typeof p.featured_image === 'string' ? p.featured_image : '')
        || p.image
        || '',
      /* No vendor line. It is this shop on every row, so it said the same
         nothing six times down the panel. */
      meta: '',
      price: money(p.price),
    }));

    const queries = (r.queries || []).slice(0, 3)
      .map((q) => ({ title: q.text, url: q.url, icon: ICON_TERM }));
    const collections = (r.collections || []).slice(0, 2)
      .map((c) => ({ title: c.title, url: c.url, icon: ICON_COLL }));

    if (!products.length && !queries.length && !collections.length) {
      renderEmpty(query);
      return;
    }

    let html = '';
    if (queries.length) html += group('Suggestions', queries.map(termRow).join(''));
    if (products.length) html += group('Products', products.map(productRow).join(''));
    if (collections.length) html += group('Collections', collections.map(termRow).join(''));
    body.innerHTML = html;
    setActive(-1);
  };

  /* ---------------------------------------------------------------------
     Fetching
     ------------------------------------------------------------------ */
  const rows = () => [...body.querySelectorAll('.bb-search__row, .bb-search__chip')];

  const setActive = (i) => {
    const list = rows();
    active = i;
    list.forEach((el, n) => el.classList.toggle('is-active', n === i));
    field.setAttribute('aria-expanded', String(list.length > 0));
    if (i >= 0 && list[i]) list[i].scrollIntoView({ block: 'nearest' });
  };

  const search = async (query) => {
    if (allLink) allLink.href = `/search?q=${encodeURIComponent(query)}`;

    if (!query.trim()) {
      overlay.classList.remove('is-loading');
      renderResting();
      return;
    }

    // One request in flight. Without this, a slow early keystroke can land after
    // a fast later one and paint results for a query nobody is looking at.
    controller?.abort();
    controller = new AbortController();
    overlay.classList.add('is-loading');

    const url = `/search/suggest.json?q=${encodeURIComponent(query)}`
      + '&resources[type]=product,collection,query&resources[limit]=6'
      + '&resources[options][fields]=title,product_type,variants.title,vendor';

    try {
      const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('search');
      renderResults(await res.json(), query);
    } catch (e) {
      if (e.name === 'AbortError') return;
      // Never leave the panel silent: hand the shopper the full search page.
      body.innerHTML = `
        <div class="bb-search__empty">
          <h3>Search is not responding</h3>
          <p>Press Enter to open the full results page for “${esc(query)}”.</p>
        </div>`;
    } finally {
      overlay.classList.remove('is-loading');
    }
  };

  /* ---------------------------------------------------------------------
     Open / close
     ------------------------------------------------------------------ */
  const scroller = () => document.querySelector('.page-wrapper') || document.scrollingElement;

  const open = (seed = '') => {
    if (!overlay.hidden) return;
    lastFocus = document.activeElement;
    overlay.hidden = false;
    // Force a frame so the transition runs from the closed state.
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    const sc = scroller();
    if (sc) { sc.dataset.bbLocked = sc.style.overflow || ''; sc.style.overflow = 'hidden'; }
    field.value = seed;
    renderResting();
    if (seed) search(seed);
    field.focus();
    field.select();
  };

  const close = () => {
    if (overlay.hidden) return;
    overlay.classList.remove('is-open');
    const sc = scroller();
    if (sc) { sc.style.overflow = sc.dataset.bbLocked || ''; delete sc.dataset.bbLocked; }
    controller?.abort();
    // Wait for the fade before removing it from the tree.
    setTimeout(() => { overlay.hidden = true; }, 300);
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  field.addEventListener('input', () => {
    clearTimeout(debounce);
    // Long enough that a fast typist sends one request rather than eight, short
    // enough that the panel never feels like it is thinking.
    debounce = setTimeout(() => search(field.value), 180);
  });

  form.addEventListener('submit', (e) => {
    const list = rows();
    if (active >= 0 && list[active]) {
      e.preventDefault();
      list[active].click();
    }
    // Otherwise the native GET to /search runs, which is the right fallback.
  });

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-bb-search-close]')?.addEventListener('click', close);

  body.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('[data-bb-search-forget]')) {
      try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
      renderResting();
    }
  });

  document.addEventListener('keydown', (e) => {
    const openable = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
    const target = e.target;
    const typing = target instanceof HTMLElement
      && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

    if (openable) { e.preventDefault(); e.stopPropagation(); open(); return; }
    if (e.key === '/' && !typing && overlay.hidden) { e.preventDefault(); open(); return; }
    if (overlay.hidden) return;

    if (e.key === 'Escape') { e.preventDefault(); close(); return; }

    const list = rows();
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(list.length ? (active + 1) % list.length : -1); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(list.length ? (active - 1 + list.length) % list.length : -1); }
    if (e.key === 'Tab') {
      // The panel owns focus while it is open.
      const focusables = [field, ...rows(), overlay.querySelector('[data-bb-search-close]')].filter(Boolean);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, true);

  /* The header field is the visible affordance; it hands over rather than
     running a second, smaller version of this. Two search experiences on one
     page is how they drift apart. */
  const headerInput = document.querySelector('[data-bb-search-input]');
  if (headerInput) {
    const takeOver = (e) => { e.preventDefault(); open(headerInput.value); headerInput.blur(); };
    headerInput.addEventListener('focus', takeOver);
    headerInput.addEventListener('mousedown', takeOver);
    headerInput.closest('form')?.addEventListener('submit', (e) => { e.preventDefault(); open(headerInput.value); });
  }

  /* The drawer's search button opens this overlay too, so a phone and a desktop
     get the same search rather than two that drift apart. Closes the drawer on
     the way, or the two would be stacked. */
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    if (!e.target.closest('[data-bb-drawer-search]')) return;
    e.preventDefault();
    document.querySelector('[data-bb-menu-close]')?.click();
    setTimeout(() => open(''), 60);
  });

  rememberProduct();
})();
